import type { HistoryDay } from "../../shared/history";
import { addDays } from "../../shared/time";
import { monthTitle, shortDate, signed } from "../format";
import { StarIcon } from "./StarIcon";
import { kidStyle } from "../kid-style";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

/** "2026-10" moved by `n` months. */
function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

/** One kid's days for the month: null while loading. Shown in the order given. */
export interface CalendarKid {
  id: string;
  name: string;
  colour: string;
  days: HistoryDay[] | null;
}

/** A date as each kid lived it; kids not added yet that day are left out. */
type Entry = { kid: CalendarKid; day: HistoryDay };

/**
 * Three steps each way of the chosen kids' net change added together, so a quiet +1 and a +12
 * day do not look alike. Null: no taps at all.
 */
function tone(entries: Entry[]): string | null {
  if (entries.every((e) => e.day.taps === 0)) return null;
  const net = entries.reduce((sum, e) => sum + e.day.net, 0);
  const size = Math.abs(net);
  if (size === 0) return "even";
  const step = size >= 10 ? 3 : size >= 5 ? 2 : 1;
  return `${net > 0 ? "up" : "down"}${step}`;
}

function status(d: HistoryDay): string {
  return d.star ? "star day" : d.closed ? "no star" : "open";
}

function cellLabel(date: string, entries: Entry[]): string {
  const kids = entries.map(({ kid, day }) => {
    const taps = day.taps === 0 ? "no taps" : `${signed(day.net)}, ${day.taps} ${day.taps === 1 ? "tap" : "taps"}`;
    return `${kid.name} ${taps}, ${status(day)}`;
  });
  return `${shortDate(date)}: ${kids.join("; ")}`;
}

/**
 * One month, Monday first, each day tinted by how far the chosen kids' scores moved together:
 * greens up, soft reds down, grey for taps that cancelled out, blank for a day with none. Under
 * the date, one mark per kid in their colour: a star for a star day, solid for a day closed
 * without one, a ring while the day is open. Days before any of them was added and after today
 * are shown but cannot be opened, and the arrows stop at both ends.
 */
export function MonthCalendar({
  month,
  kids,
  today,
  firstDay,
  onMonth,
  onPick,
}: {
  /** YYYY-MM */
  month: string;
  kids: CalendarKid[];
  today: string;
  /** The first day any of the kids existed: the arrows stop at its month. */
  firstDay: string;
  onMonth: (month: string) => void;
  onPick: (date: string) => void;
}) {
  const start = `${month}-01`;
  const lead = (new Date(start + "T12:00:00Z").getUTCDay() + 6) % 7;
  const dates: string[] = [];
  for (let d = start; d.startsWith(month); d = addDays(d, 1)) dates.push(d);
  const byKid = kids.map((kid) => ({ kid, byDay: new Map((kid.days ?? []).map((d) => [d.day, d])) }));
  const canPrev = month > firstDay.slice(0, 7);
  const canNext = month < today.slice(0, 7);

  return (
    <div className="cal">
      <div className="cal__head">
        <button
          type="button"
          className="cal__nav"
          aria-label="Previous month"
          disabled={!canPrev}
          onClick={() => onMonth(addMonths(month, -1))}
        >
          ‹
        </button>
        <h3 className="cal__title" aria-live="polite">
          {monthTitle(month)}
        </h3>
        <button
          type="button"
          className="cal__nav"
          aria-label="Next month"
          disabled={!canNext}
          onClick={() => onMonth(addMonths(month, 1))}
        >
          ›
        </button>
      </div>
      <div className="cal__grid">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="cal__weekday" aria-hidden="true">
            {w}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`lead${i}`} />
        ))}
        {dates.map((date) => {
          const n = Number(date.slice(8));
          const entries = byKid.flatMap(({ kid, byDay }) => {
            const day = byDay.get(date);
            return day ? [{ kid, day }] : [];
          });
          if (entries.length === 0) {
            return (
              <span key={date} className="cal__cell cal__cell--out" aria-hidden="true">
                {n}
              </span>
            );
          }
          return (
            <button
              key={date}
              type="button"
              className="cal__cell"
              data-tone={tone(entries) ?? undefined}
              aria-current={date === today ? "date" : undefined}
              aria-label={cellLabel(date, entries)}
              onClick={() => onPick(date)}
            >
              <span className="cal__date">{n}</span>
              <span className="cal__marks" data-many={entries.length > 3 || undefined}>
                {entries.map(({ kid, day }) => (
                  <span
                    key={kid.id}
                    className="cal__mark"
                    data-state={day.star ? "star" : day.closed ? "closed" : "open"}
                    style={kidStyle(kid.colour)}
                  >
                    {day.star && <StarIcon className="cal__mark-star" />}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
